import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ChevronRight, BookOpen, Loader2 } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { useTranslate } from "../context/TranslateContext";
import { listEditorials } from "../lib/api";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Explore() {
  const { t } = useTranslate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    let cancelled = false;
    listEditorials()
      .then((d) => { if (!cancelled) setTopics(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-paper" data-testid="explore-page">
      <InnerNav />

      {/* Header */}
      <section className="border-b border-edge">
        <div className="mx-auto max-w-[1400px] px-6 py-16 md:px-12 md:py-20">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.28em] text-gold">
              <Sparkles className="h-4 w-4" /> {t("AI Deep Dives")}
            </div>
            <h1 data-testid="explore-title" className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              {t("Deep dives into ")}<span className="italic text-gradient-gold">{t("Indian cinema")}</span>
            </h1>
            <p className="mt-5 max-w-2xl font-body text-lg leading-relaxed text-ash md:text-xl">
              {t("Long-form editorial essays on the movements, eras, music and craft that shaped Indian film — each one composed by AI and read aloud or translated into your language.")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1400px] px-6 py-14 md:px-12 md:py-16">
        {loading ? (
          <div className="flex flex-col items-center py-24 text-center" data-testid="explore-loading">
            <Loader2 className="h-7 w-7 animate-spin text-gold" />
            <p className="mt-4 font-body text-sm text-ash">{t("Loading topics…")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {topics.map((topic, i) => (
              <motion.div key={topic.slug} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
                custom={i} variants={fade}>
                <Link to={`/article/editorial-${topic.slug}`} data-testid={`deepdive-card-${topic.slug}`}>
                  <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-edge bg-surface transition-colors duration-300 hover:border-gold/50">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={topic.hero} alt="" loading="lazy"
                        className="h-full w-full object-cover opacity-80 grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
                      <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-paper/85 px-3 py-1 font-body text-xs uppercase tracking-widest text-gold backdrop-blur">
                        {t(topic.category)}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="font-display text-2xl font-semibold leading-tight text-cream">{t(topic.title)}</h3>
                      <p className="mt-2 flex-1 font-body text-sm leading-relaxed text-ash">{t(topic.blurb)}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 font-body text-sm text-gold">
                        {t("Read the deep dive")}
                        <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </motion.div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-14 flex items-center gap-3 rounded-2xl border border-edge bg-surface/40 p-6">
          <BookOpen className="h-6 w-6 shrink-0 text-gold" />
          <p className="font-body text-sm leading-relaxed text-ash">
            {t("Each essay is generated from well-documented film history and cached, then paired with a Wikipedia reference. Use Listen and Translate on any article.")}
          </p>
        </div>
      </section>
    </div>
  );
}
